import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button, CircularProgress, Stack } from '@mui/material';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import HomeIcon from '@mui/icons-material/Home';
import { useWorkplaceStore } from '@host/stores';
import { AuthService } from '@host/services/auth.service';
import { useMultiFloorStateStore } from '@/apps/office/stores/MultiFloorStore';

const getOrgSlugFromPath = (pathname: string): string | null => {
  const match = /^\/([^/]+)/.exec(pathname);
  return match ? match[1] : null;
};

const OrganizationLeave: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  // Get current and target organization slugs
  const currentOrgSlug = localStorage.getItem('org-slug');
  const targetOrgSlug = getOrgSlugFromPath(location.pathname);

  const handleConfirmLeave = async () => {
    try {
      setIsLoading(true);

      // Get workplace state for logout payload
      const workplaceState = useWorkplaceStore.getState();
      workplaceState.setInitLoading(true);

      const user_pid = workplaceState.profile?.pid;
      // Use the floor/room the user is ACTUALLY currently on, not their home
      // floor/room — otherwise the logout event broadcasts the wrong location
      // and other clients never clear the avatar from the room the user was
      // really in.
      const floor_pid =
        useMultiFloorStateStore.getState().currentFloorPresence ||
        workplaceState.currentRoom?.floor_pid ||
        workplaceState.profile?.home_floor_pid;
      const room_pid = workplaceState.currentRoom?.pid || workplaceState.profile?.home_room_pid;

      // Perform logout with proper cleanup
      if (user_pid) {
        const logoutPayload = {
          user_pid,
          floor_pid: floor_pid ?? undefined,
          room_pid: room_pid ?? undefined,
        };
        await AuthService.getInstance().handleLogout(logoutPayload);
      }

      await AuthService.getInstance().logout();

      // Navigate to the new organization URL
      if (targetOrgSlug) {
        window.location.href = `/${targetOrgSlug}`;
      } else {
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Error during organization leave:', error);
      // Even if there's an error, try to navigate to the target
      try {
        await AuthService.getInstance().logout();
        if (targetOrgSlug) {
          window.location.href = `/${targetOrgSlug}`;
        } else {
          window.location.href = '/';
        }
      } catch (cleanupError) {
        console.error('Cleanup error:', cleanupError);
        // Force navigation as last resort
        window.location.href = targetOrgSlug ? `/${targetOrgSlug}` : '/';
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleStayInOrganization = () => {
    // Navigate back to current organization
    if (currentOrgSlug) {
      navigate(`/${currentOrgSlug}/workplace`);
    } else {
      navigate('/');
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'background.default',
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 500,
          mx: 'auto',
          boxShadow: 3,
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box textAlign='center' mb={3}>
            <ErrorOutline
              sx={{
                fontSize: 64,
                color: 'primary.main',
                mb: 2,
              }}
            />
            <Typography
              variant='h4'
              component='h1'
              gutterBottom
              sx={{ fontWeight: 'bold', color: 'text.primary' }}
            >
              Leaving Organization
            </Typography>
          </Box>

          <Typography
            variant='body1'
            color='text.secondary'
            textAlign='center'
            sx={{ mb: 4, lineHeight: 1.6 }}
          >
            You are about to leave your current organization
            <strong>{currentOrgSlug && ` (${currentOrgSlug})`}</strong>. If you proceed, your
            current session will be ended and you will be redirected to{' '}
            {targetOrgSlug ? <strong>{targetOrgSlug}</strong> : <strong>the homepage</strong>}.
          </Typography>

          <Stack spacing={2}>
            <Button
              variant='contained'
              color='error'
              size='large'
              fullWidth
              onClick={handleConfirmLeave}
              disabled={isLoading}
              startIcon={
                isLoading ? <CircularProgress size={20} color='inherit' /> : <ExitToAppIcon />
              }
              sx={{ py: 1.5 }}
            >
              {isLoading ? 'Leaving Organization...' : 'Yes, Leave Organization'}
            </Button>

            <Button
              variant='outlined'
              color='primary'
              size='large'
              fullWidth
              onClick={handleStayInOrganization}
              disabled={isLoading}
              startIcon={<HomeIcon />}
              sx={{ py: 1.5 }}
            >
              No, Stay in My Organization
            </Button>
          </Stack>

          {currentOrgSlug && (
            <Typography
              variant='caption'
              color='text.secondary'
              textAlign='center'
              sx={{ mt: 3, display: 'block' }}
            >
              Current organization: <strong>{currentOrgSlug}</strong>
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrganizationLeave;
